import { useEffect, useState } from "react";
import { Calendar as CalendarIcon, CheckCircle2, RefreshCw, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * CalendarConnectionsPanel — manages per-provider calendar settings.
 *
 * Renders one block per provider (Google + Outlook today, iCloud later)
 * showing every calendar the connected account exposes, with two toggles:
 *   • Blocks availability — Bloq considers these events when finding
 *     free slots.
 *   • Write here — new Bloq bookings get pushed to this calendar.
 *
 * Each provider's UI is identical and reads from a provider config
 * object that lists the endpoint paths. Adding iCloud (CalDAV) later
 * means appending one config entry, not duplicating the UI.
 */

// Each provider exposes the same 4 endpoints (relative to /api).
// `name` shows in the panel header, `connectHref` is where the user goes
// to wire up the OAuth flow if they haven't already.
const PROVIDERS = [
  {
    id: "google",
    name: "Google Calendar",
    listPath: "/integrations/google-calendar/calendars",
    settingsPath: "/integrations/google-calendar/settings",
    syncPath: "/integrations/google-calendar/sync",
    notConnectedError: "google_calendar_not_connected",
    notConfiguredError: "google_calendar_not_configured",
    syncSupported: true,
  },
  {
    id: "outlook",
    name: "Microsoft Outlook",
    listPath: "/integrations/outlook/calendars",
    settingsPath: "/integrations/outlook/settings",
    syncPath: null,  // Outlook uses webhook subscriptions (renewed by cron); no manual button yet
    notConnectedError: "outlook_not_connected",
    notConfiguredError: "outlook_not_configured",
    syncSupported: false,
  },
];

export default function CalendarConnectionsPanel({ workspace, userTimezone }) {
  return (
    <div className="flex flex-col gap-4" data-testid="calendar-connections-panel">
      {PROVIDERS.map((p) => (
        <ProviderCalendars
          key={p.id}
          provider={p}
          workspace={workspace}
          userTimezone={userTimezone}
        />
      ))}
    </div>
  );
}

function ProviderCalendars({ provider, workspace, userTimezone }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!workspace) return;
    setLoading(true);
    setError("");
    try {
      const r = await api.get(provider.listPath, {
        params: { workspace_id: workspace.workspace_id },
      });
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "";
      const status = e?.response?.status;
      if (msg === provider.notConnectedError || status === 404) {
        setError("not_connected");
      } else if (msg === provider.notConfiguredError || status === 503) {
        setError("not_configured");
      } else {
        setError(msg || "failed_to_load");
      }
    } finally {
      setLoading(false);
    }
  };
  // Reload when the workspace changes — each workspace has its own
  // OAuth connections.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [workspace]);

  const toggleAvailability = async (calId) => {
    if (!data) return;
    const current = new Set(data.availability_calendar_ids || []);
    if (current.has(calId)) current.delete(calId);
    else current.add(calId);
    const next = current.size ? [...current] : ["primary"];
    setSaving(true);
    try {
      await api.patch(provider.settingsPath, {
        availability_calendar_ids: next,
      }, { params: { workspace_id: workspace.workspace_id } });
      toast.success("Availability sources updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update settings");
    } finally {
      setSaving(false);
    }
  };

  const setWriteCalendar = async (calId) => {
    setSaving(true);
    try {
      await api.patch(provider.settingsPath, {
        write_calendar_id: calId,
      }, { params: { workspace_id: workspace.workspace_id } });
      toast.success("Write calendar updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update settings");
    } finally {
      setSaving(false);
    }
  };

  const runSync = async () => {
    if (!provider.syncSupported) return;
    setSyncing(true);
    try {
      const r = await api.post(provider.syncPath, null, {
        params: { workspace_id: workspace.workspace_id },
      });
      toast.success(`Synced ${r.data.calendars_synced} calendar(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // If this provider isn't connected for the current workspace, we
  // hide the entire block — keeps the panel clean for users who only
  // use one provider. Errors and the connected state both stay visible.
  if (!loading && error === "not_connected") return null;
  if (!loading && error === "not_configured") return null;

  return (
    <div className="bloq-card p-5" data-testid={`calendar-panel-${provider.id}`}>
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="bloq-headline text-lg flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" /> {provider.name}
          </p>
          <p className="text-xs text-[#82828c] mt-1">
            Choose which calendars block availability and where new Bloq bookings get written.
          </p>
        </div>
        {data && provider.syncSupported && (
          <button
            onClick={runSync}
            disabled={syncing}
            className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
            data-testid={`cal-sync-now-btn-${provider.id}`}
          >
            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Sync now
          </button>
        )}
      </div>

      {loading && (
        <div className="text-sm text-[#82828c] py-6 text-center" data-testid={`cal-loading-${provider.id}`}>
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading calendars…
        </div>
      )}

      {!loading && error && !["not_connected", "not_configured"].includes(error) && (
        <div className="text-sm text-[#c43838] py-3 flex items-start gap-2" data-testid={`cal-error-${provider.id}`}>
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          Could not load calendars: {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <p className="text-xs text-[#82828c] mb-3">
            Signed in as <span className="font-medium text-[#1a1a1f]">{data.connected_email}</span>
            {data.calendars?.length > 0 && <> · {data.calendars.length} calendar(s) available</>}
            {userTimezone && <> · Your timezone: <span className="font-medium text-[#1a1a1f]">{userTimezone}</span></>}
          </p>
          <div className="flex flex-col gap-2">
            {(data.calendars || []).map((c) => {
              const blocking = c.is_blocking_availability;
              const isWrite = c.is_write_target;
              return (
                <div
                  key={c.id}
                  className="flex items-center justify-between bg-[#f5efe6] p-3 rounded-md flex-wrap gap-2"
                  data-testid={`cal-row-${provider.id}-${c.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: c.background_color || "#005e7a" }}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" title={c.summary}>
                        {c.summary}
                        {c.primary && <span className="bloq-pill ml-2 text-[10px]">Primary</span>}
                        {c.access_role === "freeBusyReader" && (
                          <span className="bloq-pill ml-2 text-[10px]">Free/busy only</span>
                        )}
                        {!c.can_write && c.access_role !== "freeBusyReader" && (
                          <span className="bloq-pill ml-2 text-[10px]">Read-only</span>
                        )}
                      </p>
                      <p className="text-[11px] text-[#82828c]">
                        {c.access_role || "—"}{c.time_zone ? ` · ${c.time_zone}` : ""}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <label
                      className="flex items-center gap-1.5 text-xs cursor-pointer select-none"
                      data-testid={`cal-block-toggle-${provider.id}-${c.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!blocking}
                        disabled={saving}
                        onChange={() => toggleAvailability(c.id)}
                        className="h-3.5 w-3.5"
                      />
                      Blocks availability
                    </label>
                    <button
                      type="button"
                      disabled={!c.can_write || saving || isWrite}
                      onClick={() => setWriteCalendar(c.id)}
                      className={`text-xs px-2 py-1 rounded ${
                        isWrite ? "bg-[#005e7a] text-white" :
                        c.can_write ? "bloq-btn-secondary" :
                        "opacity-40 cursor-not-allowed bg-white border border-[#1a1a1f]/10"
                      }`}
                      title={c.can_write ? "Write Bloq bookings here" : "You don't have write access to this calendar"}
                      data-testid={`cal-write-toggle-${provider.id}-${c.id}`}
                    >
                      {isWrite ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Write here</span> : "Write here"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
